'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { CartEmptyIcon } from '@/ui/common/Icons/CartEmptyIcon';
import { FormButton } from '@/ui/common/Buttons/FormButton';
import { BrandLoader } from '@/ui/common/Loaders/BrandLoader';
import { IconButton } from '@/ui/common/Buttons/IconButton';
import { CloseIcon } from '@/ui/common/Icons/CloseIcon';
import { useCountdown } from '@/service/hooks/useCountdown';

const EMPTY_CART_TIME_LEFT = 30;

export const CartEmpty = ({ partnerSlug }: { partnerSlug: string }) => {
    const { push } = useRouter();

    const [isOpen, setIsOpen] = useState(true);

    const handleModalClose = useCallback(() => {
        push(`/${partnerSlug}`);
        setIsOpen(false);
    }, [partnerSlug, push]);

    const { seconds } = useCountdown(EMPTY_CART_TIME_LEFT, handleModalClose);

    if (!isOpen) {
        return <BrandLoader />;
    }

    return (
        <div
            className={`min-h-screen w-full bg-cover bg-center bg-no-repeat px-4 py-10 md:px-8 ${
                isOpen && 'bg-cart-empty'
            }`}
        >
            <div
                className="fixed bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-black-1 pt-16"
                onClick={handleModalClose}
            >
                <div
                    className="absolute max-h-max w-full max-w-[340px] rounded-[15px] bg-white px-[30px] pb-[30px] pt-5 dark:bg-dark-3 md:max-w-[550px] md:p-[30px] lg:max-w-[659px] lg:p-[45px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <IconButton
                        icon={<CloseIcon />}
                        className="absolute right-3 top-3 rounded-full"
                        onClick={handleModalClose}
                    />
                    <div className="mx-auto max-w-[380px] text-center">
                        <CartEmptyIcon className="mx-auto mb-5" />

                        <h1 className="tx-large-medium mb-3 text-gray-1 dark:text-white">
                            Your cart is empty!
                        </h1>

                        <h6 className="tx-large-regular mb-7 text-gray-3 dark:text-dark-1">
                            Lets find some wonderful activities to do. Get ready to be inspired...
                        </h6>

                        <FormButton
                            type="button"
                            className="mx-auto h-[50px] w-full max-w-full px-0 md:h-[55px] lg:h-[50px]"
                            label={
                                <div className="flex w-full items-center justify-center space-x-1">
                                    <span>Sending you home in</span>
                                    <span className="w-5">{seconds}</span>
                                </div>
                            }
                            onClick={handleModalClose}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
